import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  CheckCircle, 
  Cancel,
  ArrowBack 
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';

interface PasswordCriteria {
  minLength: boolean;
  hasUppercase: boolean;
  hasSpecialChar: boolean;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  const [passwordCriteria, setPasswordCriteria] = useState<PasswordCriteria>({
    minLength: false,
    hasUppercase: false,
    hasSpecialChar: false
  });

  // Vérifier la validité du token au chargement
  useEffect(() => {
    if (!token) {
      setError(t('auth.invalidResetLink'));
      setTokenValid(false);
      return;
    }

    const verifyToken = async () => {
      try {
        await api.post('/auth/verify-reset-token', { token });
        setTokenValid(true);
      } catch (err: any) {
        console.error('Token verification error:', err);
        setError(t('auth.invalidOrExpiredToken'));
        setTokenValid(false);
      }
    };

    verifyToken();
  }, [token, t]);

  // Valider les critères du mot de passe
  useEffect(() => {
    setPasswordCriteria({
      minLength: password.length >= parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6'),
      hasUppercase: /[A-Z]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    });
  }, [password]);

  const isPasswordValid = () => {
    return passwordCriteria.minLength && 
           passwordCriteria.hasUppercase && 
           passwordCriteria.hasSpecialChar;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError(t('auth.passwordRequired'));
      return;
    }

    if (!isPasswordValid()) {
      setError(t('auth.passwordCriteriaNotMet'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      await api.post('/auth/reset-password', { 
        token, 
        password 
      });
      setSuccess(true);
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(
        err.response?.data?.detail || 
        t('auth.resetPasswordError')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenValid === false) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              width: '100%', 
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <Cancel 
              sx={{ 
                fontSize: 64, 
                color: 'error.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" component="h1" gutterBottom>
              {t('auth.invalidResetLink')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('auth.invalidResetLinkMessage')}
            </Typography>
            <Button
              component={Link}
              to="/forgot-password"
              variant="contained"
              sx={{ mr: 2 }}
            >
              {t('auth.requestNewLink')}
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<ArrowBack />}
            >
              {t('auth.backToLogin')}
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              width: '100%', 
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CheckCircle 
              sx={{ 
                fontSize: 64, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" component="h1" gutterBottom>
              {t('auth.passwordResetSuccess')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('auth.passwordResetSuccessMessage')}
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="contained"
            >
              {t('auth.goToLogin')}
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (tokenValid === null) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            width: '100%', 
            borderRadius: 2 
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('auth.resetPasswordTitle')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('auth.resetPasswordSubtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.newPassword')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Critères de validation du mot de passe */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('auth.passwordCriteria')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {passwordCriteria.minLength ? 
                      <CheckCircle color="success" fontSize="small" /> : 
                      <Cancel color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={t('auth.minLength12', { minLength: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6') })}
                    sx={{
                      color: passwordCriteria.minLength ? 'success.main' : 'error.main'
                    }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {passwordCriteria.hasUppercase ? 
                      <CheckCircle color="success" fontSize="small" /> : 
                      <Cancel color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('auth.hasUppercase')}
                    sx={{ 
                      color: passwordCriteria.hasUppercase ? 'success.main' : 'error.main' 
                    }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {passwordCriteria.hasSpecialChar ? 
                      <CheckCircle color="success" fontSize="small" /> : 
                      <Cancel color="error" fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('auth.hasSpecialChar')}
                    sx={{ 
                      color: passwordCriteria.hasSpecialChar ? 'success.main' : 'error.main' 
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                backgroundColor: isPasswordValid() && password === confirmPassword ? 
                  'primary.main' : 'grey.400'
              }}
              disabled={isLoading || !isPasswordValid() || password !== confirmPassword}
            >
              {isLoading ? <CircularProgress size={24} /> : t('auth.resetPassword')}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link 
                to="/login" 
                style={{ 
                  textDecoration: 'none', 
                  color: 'primary.main',
                  fontSize: '0.875rem'
                }}
              >
                {t('auth.backToLogin')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;